import { z } from 'zod';
import type { CodexNotification, ImageGenerationEvent, ImageGenerationPreview } from '@reados/contracts';
import type { ResolvedReference } from './references';
import { imageCommandReference } from './image-command-preview.ts';
export { mergeImageCommandEvents } from './image-command-preview.ts';

const identity = z.string().min(1).max(200).regex(/^[^\u0000-\u001f\u007f]+$/);
const outputId = z.string().min(1).max(128).regex(/^[A-Za-z0-9_-]+$/);
const time = z.number().int().nonnegative().safe();
const imageSchema = z.object({
  outputId, path: z.string(), sha256: z.string().regex(/^[0-9a-f]{64}$/),
  bytes: z.number().int().positive().max(64 * 1024 * 1024), mimeType: z.literal('image/png'),
  width: z.number().int().positive().max(32768), height: z.number().int().positive().max(32768),
}).strict().refine(image => image.path === `/workspace/.reados/generated/${image.outputId}.png`);
const previewSchema = z.object({
  receiptId: identity, ownerThreadId: identity, threadId: identity, turnId: identity, itemId: identity,
  sourceKind: z.enum(['mcp', 'hosted']), status: z.enum(['running', 'completed', 'failed']),
  revision: z.number().int().positive().safe(), startedAt: time, completedAt: time.optional(),
  sourceOrder: z.number().int().nonnegative().safe().optional(), image: imageSchema.optional(),
  error: z.object({ code: z.string().min(1).max(200), message: z.string().min(1).max(2000) }).strict().optional(),
}).strict().refine(value => value.status === 'completed' ? Boolean(value.image) && !value.error
  : value.status === 'failed' ? !value.image || value.error?.code === 'IMAGE_DELIVERY_FAILED' : !value.image)
  .refine(value => value.status !== 'failed' || Boolean(value.error))
  .refine(value => value.completedAt === undefined || value.completedAt >= value.startedAt);

export const imageGenerationKey = (threadId: string, turnId: string, itemId: string) => JSON.stringify([threadId, turnId, itemId]);
const sourceIdentity = (value: ImageGenerationPreview) => JSON.stringify([value.ownerThreadId, value.threadId, value.turnId, value.itemId, value.sourceKind, value.startedAt]);
const equal = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const terminal = (value: ImageGenerationPreview) => value.status !== 'running';

/** Shared GET/SSE reconciliation. Retain two conflicting witnesses so the
 * projection fails closed; never let a stale snapshot regress a terminal card. */
export function mergeImageGenerationEvents(current: CodexNotification[], incoming: CodexNotification[], ownerThreadId?: string): ImageGenerationEvent[] {
  const receipts = new Map<string, ImageGenerationEvent[]>();
  for (const event of [...current, ...incoming]) {
    if (event.method !== 'reados/imageGeneration') continue;
    const parsed = previewSchema.safeParse(event.params);
    if (!parsed.success || ownerThreadId !== undefined && parsed.data.ownerThreadId !== ownerThreadId) continue;
    const next: ImageGenerationEvent = { method: 'reados/imageGeneration', params: parsed.data };
    const previous = receipts.get(next.params.receiptId);
    if (!previous) { receipts.set(next.params.receiptId, [next]); continue; }
    if (previous.length > 1 || equal(previous[0].params, next.params)) continue;
    const a = previous[0].params, b = next.params;
    const conflict = sourceIdentity(a) !== sourceIdentity(b)
      || a.revision === b.revision
      || terminal(a) && terminal(b) && (a.status !== b.status || !equal(a.image, b.image));
    if (conflict) { previous.push(next); continue; }
    if (b.revision > a.revision && (!terminal(a) || terminal(b))) receipts.set(b.receiptId, [next]);
  }
  return [...receipts.values()].flat();
}

/** No shell text, model prose or browser path can create a generated image. */
export function imageGenerationPreviews(events: CodexNotification[]): Map<string, ImageGenerationPreview> {
  const receipts = mergeImageGenerationEvents([], events), duplicates = new Set<string>(), counts = new Map<string, number>();
  for (const event of receipts) counts.set(event.params.receiptId, (counts.get(event.params.receiptId) || 0) + 1);
  const previews = new Map<string, ImageGenerationPreview>();
  for (const { params } of receipts) {
    if (counts.get(params.receiptId) !== 1) continue;
    const key = imageGenerationKey(params.threadId, params.turnId, params.itemId);
    if (duplicates.has(key)) continue;
    if (previews.has(key)) { previews.delete(key); duplicates.add(key); continue; }
    previews.set(key, params);
  }
  return previews;
}

export function compareImageGenerations(a: ImageGenerationPreview, b: ImageGenerationPreview): number {
  return (a.sourceOrder !== undefined && b.sourceOrder !== undefined ? a.sourceOrder - b.sourceOrder : a.startedAt - b.startedAt)
    || a.startedAt - b.startedAt || a.itemId.localeCompare(b.itemId);
}

/** Presentation-only activity. It deliberately has no fabricated native type. */
export function imageGenerationActivity(preview: ImageGenerationPreview, nativeType?: string): Record<string, unknown> {
  const image = preview.image, running = preview.status === 'running', failed = preview.status === 'failed';
  const deliveryFailed = failed && preview.error?.code === 'IMAGE_DELIVERY_FAILED';
  const failureText = deliveryFailed ? image ? '图片已保存，但回传未完成。可预览已有图片，并检查服务状态。'
    : '图片已生成，但保存或交付未完成，请检查服务状态。' : '图片工具未能完成，请检查服务状态。';
  return {
    id: `reados-image-generation:${preview.receiptId}`, type: 'tool_call',
    ...(nativeType ? { readexNativeItemType: nativeType } : {}),
    readosImageGenerationReceiptId: preview.receiptId, readosImageGenerationSource: preview.sourceKind,
    ...(deliveryFailed && image ? { readosImageGenerationDeliveryFailed: true } : {}),
    sourceBlockId: `reados-activity:${imageGenerationKey(preview.threadId, preview.turnId, preview.itemId)}`,
    tool: 'image_generation', readexToolName: 'image_generation',
    text: running ? '正在生成图片' : deliveryFailed ? image ? '图片已保存，回传失败' : '图片已生成，交付失败' : failed ? '图片生成失败' : '已生成 1 张图片',
    status: running ? 'running' : failed ? 'error' : 'success', completed: !running,
    startedAtMilliseconds: preview.startedAt,
    ...(preview.completedAt === undefined ? {} : { completedAtMilliseconds: preview.completedAt }),
    arguments: {}, result: { sourceKind: preview.sourceKind, status: preview.status, receiptId: preview.receiptId },
    detailText: failed ? `${failureText}（${preview.error!.code}）` : '', error: failed ? failureText : '',
    summaryParts: [], searchQueries: [], searchReferences: [], webSearchActions: [], childItems: [],
    previewItems: image ? [{ id: `${preview.receiptId}:${image.outputId}`, kind: 'file', title: '生成的图片',
      filePath: image.path, fileName: `${image.outputId}.png`, mimeType: image.mimeType, attachmentKind: 'image',
      payload: { preview_kind: 'image_generation', image_path: image.path, content_id: image.outputId,
        reados_verified_image_generation: true, readosImageGenerationReceiptId: preview.receiptId,
        readosImageGenerationOutputId: image.outputId, width: image.width, height: image.height } }] : [],
  };
}

/** Generated paths are capabilities only through the matching verified receipt.
 * Missing/conflicting evidence never falls back to an ordinary path ticket. */
export function generatedImageReference(reference: ResolvedReference | null, events: CodexNotification[]): ResolvedReference | null {
  reference = imageCommandReference(reference, events);
  if (!reference || !reference.target.path?.startsWith('/workspace/.reados/generated/')) return reference;
  const matches = [...imageGenerationPreviews(events).values()].filter(value => value.image?.path === reference.target.path);
  if (matches.length !== 1) return null;
  const preview = matches[0];
  return { ...reference, kind: 'image', target: { ...reference.target,
    imageGenerationReceiptId: preview.receiptId, imageGenerationOutputId: preview.image!.outputId } };
}
